export function appendMiscellaneous(reagents, report, options = {}) {
  const { miscellaneous = {} } = options;
  const {
    highVoltage,
    dustRisk,
    reactionTime,
    temperature,
    gasLines,
    pressure,
    overheadStirring,
  } = miscellaneous;

  const results = [];

  results.push(getDustRisk(dustRisk, options));
  results.push(getReactionTime(reactionTime, options));
  results.push(getTemperature(temperature, options));
  results.push(getGasLines(gasLines, options));
  results.push(getPressure(pressure, options));
  results.push(getOverheadStirring(overheadStirring, options));
  results.push(getHighVoltage(highVoltage, options));
  report.miscellaneous = results;
}

function getDustRisk(dustRisk, options) {
  const { logger } = options;
  const entry = {
    label: 'Dust risk',
    info: '(for more than 5g of solids)',
    scores: [],
  };
  switch (dustRisk) {
    case 'yes':
      entry.scores.push({
        remarks: 'Mask still required for all quantities of fine particles!',
        label: 'Fine powders, fibers air suspended',
        score: 4,
      });
      break;
    case 'none':
      entry.scores.push({
        label: 'None',
        score: 0,
      });
      break;
    default:
      logger.error(`Unknown dustRisk: ${dustRisk}`);
  }
  entry.score = entry.scores.reduce((acc, cur) => acc + cur.score, 0);
  return entry;
}

function getReactionTime(reactionTime, options) {
  const { logger } = options;
  const entry = {
    label: 'Reaction time',
    info: '',
    scores: [],
  };
  switch (reactionTime) {
    case 'daytime':
      entry.scores.push({
        label: 'Daytime',
        score: 0,
      });
      break;
    case 'overnight':
      entry.scores.push({
        label: 'Overnight',
        score: 2,
      });
      break;
    default:
      logger.error(`Unknown reactionTime: ${reactionTime}`);
  }
  entry.score = entry.scores.reduce((acc, cur) => acc + cur.score, 0);
  return entry;
}

function getTemperature(temperature, options) {
  const { logger } = options;
  const entry = {
    label: 'Temperature',
    info: '',
    scores: [],
  };
  switch (temperature) {
    case '<100':
      entry.scores.push({
        label: '< 100°C',
        score: 0,
      });
      break;
    case '100-200':
      entry.scores.push({
        label: '100-200°C',
        score: 1,
      });
      break;
    case 'extreme':
      entry.scores.push({
        label: '> 200°C or < -30°C',
        score: 2,
      });
      break;
    default:
      logger.error(`Unknown temperature: ${temperature}`);
  }
  entry.score = entry.scores.reduce((acc, cur) => acc + cur.score, 0);
  return entry;
}

function getGasLines(gasLines, options) {
  const { logger } = options;
  const entry = {
    label: 'Handling High Pressure Gas Lines',
    info: '(Gas line pressure)',
    scores: [],
  };
  const inertDescription = 'Inert (e.g., N2, He, Ar, CO2)';
  switch (gasLines.inert) {
    case 'none':
      entry.scores.push({
        description: inertDescription,
        label: 'None',
        score: 0,
      });
      break;
    case '<20':
      entry.scores.push({
        description: inertDescription,
        label: '<20 barg',
        score: 0,
      });
      break;
    case '20-40':
      entry.scores.push({
        description: inertDescription,
        label: '20-40 barg',
        score: 1,
      });
      break;
    case '>40':
      entry.scores.push({
        description: inertDescription,
        label: '>40 barg',
        score: 2,
      });
      break;
    default:
      logger.error(`Unknown gasLines.inert: ${gasLines.inert}`);
  }

  const reactiveDescription =
    'Reactive/Hazardous (e.g., CO, NH3, H2, hydrocarbons)';
  switch (gasLines.reactive) {
    case 'none':
      entry.scores.push({
        description: reactiveDescription,
        label: 'None',
        score: 0,
      });
      break;
    case '<20':
      entry.scores.push({
        description: reactiveDescription,
        label: '<20 barg',
        score: 2,
      });
      break;
    case '20-40':
      entry.scores.push({
        description: reactiveDescription,
        label: '20-40 barg',
        score: 3,
      });
      break;
    case '>40':
      entry.scores.push({
        description: reactiveDescription,
        label: '>40 barg',
        score: 4,
      });
      break;
    default:
      logger.error(`Unknown gasLines.inert: ${gasLines.reactive}`);
  }
  entry.score = entry.scores.reduce((acc, cur) => acc + cur.score, 0);
  return entry;
}

function getPressure(pressure, options) {
  const { logger } = options;
  const entry = {
    label: 'For pressurized reactions in Parr/flow',
    info: 'Did you pass the PARR / Flow reactor exam and sign the pressurized SOP and PHA?',
    scores: [],
  };
  const parrLowDescription = 'Parr < 50 mL or flow reactor(1 / 4';

  switch (pressure.parrLow) {
    case 'none':
      entry.scores.push({
        description: parrLowDescription,
        label: 'None',
        score: 0,
      });
      break;
    case '1-20':
      entry.scores.push({
        description: parrLowDescription,
        label: '1-20 barg',
        score: 1,
      });
      break;
    case '20-100':
      entry.scores.push({
        description: parrLowDescription,
        label: '20-100 barg',
        score: 2,
      });
      break;
    case '>100':
      entry.scores.push({
        description: parrLowDescription,
        label: '>100 barg',
        score: 3,
      });
      break;
    default:
      logger.error(`Unknown pressure.parrLow: ${pressure.parrLow}`);
  }

  const parrHighDescription = 'Parr > 50 mL or flow reactor(1 / 2';
  switch (pressure.parrHigh) {
    case 'none':
      entry.scores.push({
        description: parrHighDescription,
        label: 'None',
        score: 0,
      });
      break;
    case '1-2':
      entry.scores.push({
        description: parrHighDescription,
        label: '1-2 barg',
        score: 1,
      });
      break;
    case '2-5':
      entry.scores.push({
        description: parrHighDescription,
        label: '2-5 barg',
        score: 2,
      });
      break;
    case '>10':
      entry.scores.push({
        description: parrHighDescription,
        label: '>10 barg',
        score: 3,
      });
      break;
    default:
      logger.error(`Unknown pressure.parrHigh: ${pressure.parrHigh}`);
  }

  const pRatedDescription = 'Glass (P-Rated)';
  switch (pressure.pRated) {
    case 'none':
      entry.scores.push({
        description: pRatedDescription,
        label: 'None',
        score: 0,
      });
      break;
    case '1-2':
      entry.scores.push({
        description: pRatedDescription,
        label: '1-2 barg',
        score: 1,
      });
      break;
    case '2-5':
      entry.scores.push({
        description: pRatedDescription,
        label: '2-5 barg',
        score: 2,
      });
      break;
    case '>5':
      entry.scores.push({
        description: pRatedDescription,
        label: '>5 barg',
        score: 3,
      });
      break;
    default:
      logger.error(`Unknown pressure.pRated: ${pressure.pRated}`);
  }

  const nonRatedDescription = 'Glass (Not rated)';
  switch (pressure.nonRated) {
    case 'none':
      entry.scores.push({
        description: nonRatedDescription,
        label: 'None',
        score: 0,
      });
      break;
    case 'yes':
      entry.scores.push({
        description: nonRatedDescription,
        label: 'Yes',
        score: 100,
      });
      break;
    default:
      logger.error(`Unknown pressure.nonRated: ${pressure.nonRated}`);
  }
  entry.score = entry.scores.reduce((acc, cur) => acc + cur.score, 0);
  return entry;
}

function getOverheadStirring(overheadStirring, options) {
  const { logger } = options;
  const entry = {
    label: 'Overheads stirring ?',
    info: '',
    scores: [],
  };
  switch (overheadStirring) {
    case 'none':
      entry.scores.push({
        label: 'None',
        score: 0,
      });
      break;
    case 'yes':
      entry.scores.push({
        label: 'Yes',
        score: 2,
      });
      break;
    default:
      logger.error(`Unknown overheadStirring: ${overheadStirring}`);
  }
  entry.score = entry.scores.reduce((acc, cur) => acc + cur.score, 0);
  return entry;
}

function getHighVoltage(highVoltage, options) {
  const { logger } = options;
  const entry = {
    label: 'High voltage ?',
    info: '',
    scores: [],
  };
  switch (highVoltage) {
    case 'none':
      entry.scores.push({
        label: 'None',
        score: 0,
      });
      break;
    case 'yes':
      entry.scores.push({
        label: 'Yes',
        score: 100,
      });
      break;
    default:
      logger.error(`Unknown highVoltage: ${highVoltage}`);
  }
  entry.score = entry.scores.reduce((acc, cur) => acc + cur.score, 0);
  return entry;
}
